import { useState, type FormEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { authApi } from '@/api/clients/authApi'
import { accountApi } from '@/api/clients/accountApi'
import { useAuthStore, parseJwtClaims } from '@/stores/authStore'
import { useTheme } from '@/contexts/ThemeContext'
import EmbedNotice from '@/components/EmbedNotice'
import { isEmbedded } from '@/lib/embed'
import LoginBackground from '@/components/LoginBackground'

// Floating "quantum" particles for the login background (fixed set → stable render).
const LOGIN_PARTICLES = [
  { left: 8, size: 4, delay: 0, dur: 14 },
  { left: 18, size: 6, delay: 3, dur: 18 },
  { left: 27, size: 3, delay: 6, dur: 12 },
  { left: 39, size: 5, delay: 1.5, dur: 16 },
  { left: 48, size: 8, delay: 4.5, dur: 20 },
  { left: 58, size: 4, delay: 2, dur: 15 },
  { left: 67, size: 6, delay: 7, dur: 19 },
  { left: 76, size: 3, delay: 5, dur: 13 },
  { left: 85, size: 5, delay: 0.5, dur: 17 },
  { left: 93, size: 4, delay: 8, dur: 21 },
]

// A password form must never render inside a partner frame (clickjacking / phishing).
export default function Login() {
  return isEmbedded() ? <EmbedNotice state="failed" /> : <LoginForm />
}

function LoginForm() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const authStore = useAuthStore()
  const { theme, toggleTheme } = useTheme()

  const from = (location.state as { from?: Location })?.from?.pathname ?? '/'

  const mutation = useMutation({
    mutationFn: () => authApi.login({ email, password }),
    onSuccess: (data) => {
      const claims = parseJwtClaims(data.access_token)
      authStore.setSession({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: data.expires_at,
        userId: claims.uid,
        orgId: claims.oid,
        role: claims.role,
      })
      // Eagerly fetch org name + user info while the fresh token is in memory.
      accountApi
        .getOrg(claims.oid)
        .then((org) => authStore.setOrgInfo(org.name, org.plan))
        .catch(() => {})
      accountApi
        .getUser(claims.oid, claims.uid)
        .then((user) => authStore.setUserInfo(user.email, user.role))
        .catch(() => {})
      navigate(from, { replace: true })
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    mutation.mutate()
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background-light dark:bg-background-dark px-4">
      {/* Animated quantum aurora background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <LoginBackground />
        <div className="login-grid absolute inset-0" />
        <div className="login-particles absolute inset-0">
          {LOGIN_PARTICLES.map((p, i) => (
            <span
              key={i}
              className="login-particle"
              style={{
                left: `${p.left}%`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                animationDelay: `${p.delay}s`,
                animationDuration: `${p.dur}s`,
              }}
            />
          ))}
        </div>
        <div className="login-vignette absolute inset-0" />
      </div>

      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 z-10 p-2 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-primary/5 transition-colors"
        aria-label="Toggle theme"
      >
        <span className="material-icons text-xl">
          {theme === 'light' ? 'dark_mode' : 'light_mode'}
        </span>
      </button>

      <div className="relative z-10 w-full max-w-sm login-enter">
        <div className="mb-8 text-center">
          <img src="/favicon.svg" alt="Quantizant" className="login-logo mx-auto mb-4 w-20 h-20" />
          <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">Quantizant Risk</h1>
          <p className="mt-1 text-sm text-subtle-light dark:text-subtle-dark">
            Quantum Risk Intelligence
          </p>
        </div>

        <div className="bg-surface-light dark:bg-surface-dark rounded-xl border border-border-light dark:border-border-dark p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-text-light dark:text-text-dark mb-5">Sign in</h2>

          {mutation.isError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <span className="material-icons text-error text-sm mt-0.5">error</span>
              <p className="text-sm text-red-700 dark:text-red-300">
                {(mutation.error as Error)?.message?.includes('INVALID_CREDENTIALS')
                  ? 'Invalid email or password.'
                  : 'Login failed. Please try again.'}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-text-light dark:text-text-dark mb-1"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-text-light dark:text-text-dark mb-1"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <span className="material-icons text-base">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={mutation.isPending}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium"
            >
              {mutation.isPending && (
                <span className="material-icons text-base animate-spin">sync</span>
              )}
              Sign in
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
